import {
  useFormField
} from "./chunk-MJ3U6SP3.js";
import {
  FontAwesomeIcon
} from "./chunk-APS5QAFW.js";
import {
  cn,
  twVariants
} from "./chunk-LJ5F4WFH.js";

// src/Select/Select.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as React from "react";
var selectStyles = twVariants({
  slots: {
    trigger: [
      "relative flex w-full items-center justify-between rounded-lg border border-neutral bg-surface-lighter px-3 text-neutral-primary transition-all [&>span]:line-clamp-1",
      "data-[state=open]:border-brand-selected",
      "focus:border-brand-selected focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-radiant-blue focus-visible:ring-offset-2",
      "data-[placeholder]:text-neutral-secondary"
    ],
    content: [
      "relative z-50 max-h-96 min-w-96 overflow-hidden rounded-lg bg-surface-white p-1 text-neutral-primary shadow-1",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
    ],
    item: [
      "relative my-1 flex w-full cursor-default select-none items-center justify-between rounded-md px-3 py-2.5 text-body-1 outline-none",
      // Focus
      'focus:bg-neutral-ghost-hovered data-[active-item="true"]:bg-neutral-ghost-hovered',
      // Disabled
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      // Selected
      'data-[state="checked"]:bg-neutral-ghost-hovered'
    ]
  },
  variants: {
    size: {
      sm: {
        trigger: "min-h-8 text-body-1 leading-4"
      },
      md: {
        trigger: "min-h-10 text-body-1"
      },
      lg: {
        trigger: "min-h-13 text-body-1"
      }
    },
    invalid: {
      true: {
        rootTrigger: "border-alert-danger focus:border-alert-danger data-[state=open]:border-alert-danger",
        trigger: "border-alert-danger"
      }
    },
    readOnly: {
      true: {
        trigger: "focus-within:border-inherit bg-white"
      },
      false: {}
    },
    disabled: {
      true: {},
      false: {}
    }
  },
  compoundVariants: [
    {
      readOnly: false,
      disabled: true,
      className: {
        trigger: "disabled:cursor-not-allowed disabled:border-transparent disabled:text-neutral-disabled"
      }
    }
  ]
});
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React.forwardRef(({ className, size = "md", children, icon, ...props }, ref) => {
  const ownProps = useFormField(props);
  const isInvalid = !!ownProps["aria-invalid"];
  const { id } = ownProps;
  const isDisabled = ownProps?.readOnly || ownProps?.disabled;
  const { trigger } = selectStyles({
    size,
    invalid: !!isInvalid,
    readOnly: !!ownProps.readOnly,
    disabled: !!isDisabled
  });
  return /* @__PURE__ */ React.createElement(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(trigger(), className),
      id,
      disabled: isDisabled,
      ...props
    },
    children,
    /* @__PURE__ */ React.createElement(SelectPrimitive.Icon, { asChild: true }, icon ? icon : /* @__PURE__ */ React.createElement(FontAwesomeIcon, { name: "chevron-down:regular", className: "h-4 w-4" }))
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React.createElement(FontAwesomeIcon, { name: "chevron-up:regular", className: "h-4 w-4" })
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React.createElement(FontAwesomeIcon, { name: "chevron-down:regular", className: "h-4 w-4" })
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => {
  const { content } = React.useMemo(() => selectStyles(), []);
  return /* @__PURE__ */ React.createElement(SelectPrimitive.Portal, null, /* @__PURE__ */ React.createElement(
    SelectPrimitive.Content,
    {
      onCloseAutoFocus: (e) => e.preventDefault(),
      ref,
      className: cn(
        content(),
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ...props
    },
    /* @__PURE__ */ React.createElement(
      ScrollArea.Root,
      {
        className: cn(
          position === "popper" && "max-h-96 w-full min-w-[var(--radix-select-trigger-width)]"
        ),
        type: "auto"
      },
      /* @__PURE__ */ React.createElement(SelectPrimitive.Viewport, { asChild: true }, /* @__PURE__ */ React.createElement(
        ScrollArea.Viewport,
        {
          className: cn(
            "p-1",
            position === "popper" && "max-h-96 w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          style: { overflowY: void 0 }
        },
        children
      )),
      /* @__PURE__ */ React.createElement(
        ScrollArea.Scrollbar,
        {
          className: "flex touch-none select-none bg-transparent p-0.5 transition-colors duration-200 ease-out hover:bg-surface-lighter data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col",
          orientation: "vertical"
        },
        /* @__PURE__ */ React.createElement(ScrollArea.Thumb, { className: "relative flex-1 rounded-lg bg-surface-dark before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']" })
      )
    )
  ));
});
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  SelectPrimitive.Label,
  {
    ref,
    className: cn(
      "px-3 py-2.5 text-body-1 font-semibold text-neutral-primary",
      className
    ),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React.forwardRef(({ className, children, ...props }, ref) => {
  const { item } = React.useMemo(() => selectStyles(), []);
  return /* @__PURE__ */ React.createElement(
    SelectPrimitive.Item,
    {
      ref,
      className: cn(item(), className),
      ...props
    },
    /* @__PURE__ */ React.createElement(SelectPrimitive.ItemText, { className: "flex w-full flex-1" }, children),
    /* @__PURE__ */ React.createElement(SelectPrimitive.ItemIndicator, null, /* @__PURE__ */ React.createElement(
      FontAwesomeIcon,
      {
        name: "check:regular",
        className: "ml-2 h-4 w-4 text-success-alert-icon"
      }
    ))
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("bg-divider my-2 h-px", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  selectStyles,
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator
};
//# sourceMappingURL=chunk-VOIIXQII.js.map